import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import axios from 'axios';

// Import existing payment components
import RapydCheckoutForm from './RapydCheckoutForm';

interface PaymentOptionFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (subscriptionData: any) => void;
  selectedPlan: 'basic' | 'enterprise' | null;
  apiUrl: string;
}

const PaymentOptionForm: React.FC<PaymentOptionFormProps> = ({
  open,
  onClose,
  onSuccess,
  selectedPlan,
  apiUrl
}) => {
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'paypal'>('card');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  
  // For Rapyd checkout form
  const [showRapydCheckout, setShowRapydCheckout] = useState(false);
  
  // For PayPal redirect
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [showManualRedirect, setShowManualRedirect] = useState(false);
  const [paypalData, setPaypalData] = useState<{
    id: string;
    redirectUrl: string;
  } | null>(null);

  // Handle PayPal redirect
  const handlePayPalRedirect = useCallback(async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const subscriptionStatus = urlParams.get('subscription');
    const provider = urlParams.get('provider');
    
    if (subscriptionStatus === 'success' && provider === 'paypal') {
      console.log('Detected return from PayPal checkout');
      
      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Check localStorage for pending PayPal ID
      const paypalId = localStorage.getItem('pendingPaypalId');
      if (paypalId) {
        try {
          console.log('Verifying PayPal subscription:', paypalId);
          
          // Show processing UI
          setIsProcessing(true);
          
          const response = await axios.post(
            `${apiUrl}/api/payments/verify-paypal-subscription`,
            { subscriptionId: paypalId },
            { withCredentials: true }
          );
          
          if (response.data.success) {
            console.log('PayPal subscription verified successfully');
            setPaymentSuccess(true);
            
            // Clear localStorage
            localStorage.removeItem('pendingPaypalId');
            localStorage.removeItem('pendingPaypalPlan');
            localStorage.removeItem('pendingPaypalTimestamp');
            
            // Pass subscription data to parent component
            if (response.data.subscription) {
              onSuccess(response.data.subscription);
            }
            
            // Close dialog after a delay
            setTimeout(() => {
              setPaymentSuccess(false);
              onClose();
            }, 2000);
          }
        } catch (error: any) {
          console.error('Error verifying PayPal subscription:', error);
          setError(
            error.response?.data?.message || 
            'Failed to verify PayPal subscription. Please contact support.'
          );
        } finally {
          setIsProcessing(false);
        }
      }
    } else if (subscriptionStatus === 'error' && provider === 'paypal') {
      // Handle error return from PayPal
      console.log('Detected error return from PayPal');
      
      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      
      setError('PayPal subscription was cancelled or encountered an error.');
      
      // Clean up localStorage
      localStorage.removeItem('pendingPaypalId');
      localStorage.removeItem('pendingPaypalPlan');
      localStorage.removeItem('pendingPaypalTimestamp');
    }
  }, [apiUrl, onClose, onSuccess]);

  // Check URL parameters on mount
  useEffect(() => {
    handlePayPalRedirect();
  }, [handlePayPalRedirect]);

  // Also check when dialog is opened
  useEffect(() => {
    if (open) {
      handlePayPalRedirect();
    }
  }, [open, handlePayPalRedirect]);

  const handlePaymentMethodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentMethod(event.target.value as 'card' | 'paypal');
    setError(null);
  };

  const handleProceed = async () => {
    if (paymentMethod === 'card') {
      // Show Rapyd checkout form
      setShowRapydCheckout(true);
    } else if (paymentMethod === 'paypal') {
      // Process PayPal payment
      await processPayPalPayment();
    }
  };

  const processPayPalPayment = async () => {
    if (!selectedPlan) {
      setError('Please select a plan first');
      return;
    }

    setIsProcessing(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${apiUrl}/api/payments/paypal-subscription-checkout`,
        { plan: selectedPlan },
        { withCredentials: true }
      );
      
      if (response.data.success && response.data.redirectUrl) {
        setPaypalData({
          id: response.data.checkoutId,
          redirectUrl: response.data.redirectUrl
        });
        
        // Save PayPal ID to localStorage for recovery
        localStorage.setItem('pendingPaypalId', response.data.checkoutId);
        localStorage.setItem('pendingPaypalPlan', selectedPlan);
        localStorage.setItem('pendingPaypalTimestamp', Date.now().toString());
        
        console.log('PayPal checkout created successfully:', {
          id: response.data.checkoutId,
          redirectUrl: response.data.redirectUrl
        });
        
        // Show redirecting UI
        setIsRedirecting(true);
        
        // Set timeout to show manual redirect button if automatic redirect fails
        setTimeout(() => {
          setShowManualRedirect(true);
        }, 5000);
        
        // Delay slightly to ensure state updates happen before redirect
        setTimeout(() => {
          try {
            window.location.href = response.data.redirectUrl;
          } catch (redirectError) {
            console.error('Failed to redirect to PayPal:', redirectError);
            
            // Fallback approach
            const newWindow = window.open(response.data.redirectUrl, '_self');
            
            if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
              // Popup was blocked or failed to open
              setError('Unable to open PayPal. Please disable popup blockers and try again.');
              setIsProcessing(false);
              setIsRedirecting(false);
            }
          }
        }, 1000);
      } else {
        console.error('PayPal checkout creation failed:', response.data);
        setError(response.data.message || 'Failed to create PayPal checkout. Please try again.');
        setIsProcessing(false);
      }
    } catch (error: any) {
      console.error('PayPal checkout error:', error);
      setError(
        error.response?.data?.message || 
        'Failed to create PayPal checkout. Please try again.'
      );
      setIsProcessing(false);
    }
  };

  const handleManualRedirect = () => {
    if (paypalData?.redirectUrl) {
      window.location.href = paypalData.redirectUrl;
    } else {
      setError('PayPal URL not available. Please try again.');
    }
  };

  const handleClose = () => {
    setPaymentMethod('card');
    setIsProcessing(false);
    setError(null);
    setIsRedirecting(false);
    setShowManualRedirect(false);
    setPaypalData(null);
    setPaymentSuccess(false);
    onClose();
  };

  const handleRapydClose = () => {
    setShowRapydCheckout(false);
  };

  return (
    <>
      <Dialog 
        open={open && !showRapydCheckout} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {paymentSuccess ? 'Payment Successful!' : 'Select Payment Method'}
        </DialogTitle>
        
        <DialogContent>
          {paymentSuccess ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircleOutlineIcon color="success" sx={{ fontSize: 60 }} />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Thank you for your subscription!
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                Your subscription has been activated successfully.
              </Typography>
            </Box>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              
              <Typography variant="body1" sx={{ mb: 2 }}>
                You are subscribing to the <strong>{selectedPlan === 'basic' ? 'Basic' : 'Enterprise'} Plan</strong> for{' '}
                <strong>${selectedPlan === 'basic' ? '20' : '40'} per month</strong>.
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                This is a recurring monthly subscription. You will be charged automatically 
                each month until you cancel. You can cancel anytime from your account dashboard.
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="body1" sx={{ mb: 2, fontWeight: 'medium' }}>
                Please select your preferred payment method:
              </Typography>
              
              <RadioGroup
                aria-label="payment-method"
                name="payment-method"
                value={paymentMethod}
                onChange={handlePaymentMethodChange}
              >
                <FormControlLabel 
                  value="card" 
                  control={<Radio />} 
                  label="Card Payment" 
                  disabled={isProcessing}
                />
                <FormControlLabel 
                  value="paypal" 
                  control={<Radio />} 
                  label="PayPal" 
                  disabled={isProcessing}
                />
              </RadioGroup>
              
              {isProcessing && (
                <Box sx={{ textAlign: 'center', mt: 3 }}>
                  <CircularProgress size={30} />
                  <Typography variant="body1" sx={{ mt: 2 }}>
                    {isRedirecting 
                      ? 'Redirecting to PayPal...' 
                      : 'Processing payment...'}
                  </Typography>
                  
                  {isRedirecting && showManualRedirect && (
                    <Box sx={{ mt: 3 }}>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        If you're not automatically redirected to PayPal, please click the button below.
                      </Alert>
                      <Button 
                        variant="contained"
                        color="primary"
                        onClick={handleManualRedirect}
                      >
                        Go to PayPal
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        
        <DialogActions>
          {!paymentSuccess && !isProcessing && (
            <>
              <Button onClick={handleClose} color="inherit">
                Cancel
              </Button>
              <Button 
                onClick={handleProceed} 
                variant="contained" 
                color="primary"
                disabled={isProcessing}
              >
                Continue with {paymentMethod === 'card' ? 'Card' : 'PayPal'}
              </Button>
            </>
          )}
          {paymentSuccess && (
            <Button onClick={handleClose} color="primary">
              Continue
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Rapyd Checkout Form */}
      <RapydCheckoutForm
        open={showRapydCheckout}
        onClose={handleRapydClose}
        onSuccess={onSuccess}
        selectedPlan={selectedPlan}
        apiUrl={apiUrl}
      />
    </>
  );
};

export default PaymentOptionForm; 